import React from 'react';
import { Form } from 'react-bootstrap';
import MultiSelectDropdown from '../../layout/MultiSelectDropdown1';
import {
  REPEAT_FREQUENCY_OPTIONS,
  REPEAT_WEEKDAY_OPTIONS,
  MIN_MINUTELY_REPEAT_INTERVAL,
  repeatIntervalUnitLabel,
} from './schedulerRepeatUi';

/**
 * Task / EditLayout-style repeat controls (repeat_frequency, repeat_on_day, repeat_at, interval).
 */
export default function SchedulerRepeatFields({
  repeatFrequency,
  repeatInterval,
  repeatOnDayKeys,
  repeatAt,
  onChange,
  disabled,
}) {
  const isRecurring = repeatFrequency && repeatFrequency !== 'once';
  const isWeekly = repeatFrequency === 'weekly';
  const isSubDaily =
    repeatFrequency === 'minutely' || repeatFrequency === 'hourly';
  const intervalUnit = repeatIntervalUnitLabel(repeatFrequency);

  return (
    <>
      <Form.Group className="mb-3">
        <Form.Label>Repeat</Form.Label>
        <Form.Select
          value={repeatFrequency || 'once'}
          onChange={(e) => onChange('repeat_frequency', e.target.value)}
          disabled={disabled}
        >
          {REPEAT_FREQUENCY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Form.Select>
        <Form.Text className="text-muted">
          Minutes and hours repeat from the start time; day/week/month use repeat at when set.
        </Form.Text>
      </Form.Group>

      {isRecurring ? (
        <Form.Group className="mb-3">
          <Form.Label>Every</Form.Label>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <Form.Control
              type="number"
              min={
                repeatFrequency === 'minutely'
                  ? MIN_MINUTELY_REPEAT_INTERVAL
                  : repeatFrequency === 'hourly'
                    ? 1
                    : 1
              }
              max={repeatFrequency === 'minutely' ? 10080 : repeatFrequency === 'hourly' ? 8760 : undefined}
              style={{ maxWidth: '6rem' }}
              value={
                repeatInterval != null
                  ? String(repeatInterval)
                  : repeatFrequency === 'minutely'
                    ? String(MIN_MINUTELY_REPEAT_INTERVAL)
                    : '1'
              }
              onChange={(e) => onChange('repeat_interval', e.target.value)}
              disabled={disabled}
            />
            {intervalUnit ? (
              <span className="text-muted small">{intervalUnit}</span>
            ) : null}
          </div>
          {isSubDaily ? (
            <Form.Text className="text-muted">
              First run uses start / next run above; then every{' '}
              {repeatInterval ||
                (repeatFrequency === 'minutely'
                  ? String(MIN_MINUTELY_REPEAT_INTERVAL)
                  : '1')}{' '}
              {intervalUnit || 'interval'}.
              {repeatFrequency === 'minutely'
                ? ` Minimum ${MIN_MINUTELY_REPEAT_INTERVAL} minutes.`
                : null}
            </Form.Text>
          ) : null}
        </Form.Group>
      ) : null}

      {isWeekly ? (
        <Form.Group className="mb-3">
          <Form.Label>Repeat on (days of week)</Form.Label>
          <MultiSelectDropdown
            selectedValues={repeatOnDayKeys || []}
            picklistValues={REPEAT_WEEKDAY_OPTIONS.map((o) => ({
              key: o.key,
              value: o.label,
            }))}
            onChange={(selected) => onChange('repeat_on_day_keys', selected)}
            disabled={disabled}
            placeholder="Select days…"
          />
          <Form.Text className="text-muted">
            Stored like task <code>repeat_on_day</code> (e.g. sun;mon;fri).
          </Form.Text>
        </Form.Group>
      ) : null}

      {!isSubDaily ? (
        <Form.Group className="mb-3">
          <Form.Label>Repeat at (time)</Form.Label>
          <Form.Control
            type="time"
            value={repeatAt || ''}
            onChange={(e) => onChange('repeat_at', e.target.value)}
            disabled={disabled}
          />
          <Form.Text className="text-muted">
            Optional. Applied to daily and longer repeats (same as task <code>repeat_at</code>).
          </Form.Text>
        </Form.Group>
      ) : null}
    </>
  );
}
